import { useEffect, useState } from 'react';
import type { SafetyNotice } from '@buildmate/core';
import {
  acknowledgeSafetyNotice,
  acknowledgedIds,
  subscribeToSafetyNotices,
  unacknowledged,
} from '@buildmate/core';
import { Button, Modal } from '@buildmate/ui-web';
import { usePorts } from '../lib/ports-context.tsx';
import { useSession } from '../lib/session-context.tsx';

/**
 * The notice a person has to acknowledge before carrying on (SCRUM-208).
 *
 * Mounted by **both** the internal app and the sub portal, because a sub on
 * site arguably needs a safety reminder more than an office user does, and
 * the audience on the notice decides which of them sees it. Not the client
 * portal: a homeowner has no safety obligation to the company, and the
 * entity refuses `client` in an audience at all.
 *
 * **Dismissible only by acknowledging.** No close button, no Escape, no
 * click-away. That is the difference between this and a cookie banner, and
 * it is the whole reason the feature was asked for — a notice people can
 * click past is a notice nobody reads. It *is* skippable in one sense,
 * deliberately: nothing blocks the page beneath if the read fails, because
 * locking somebody out of their work over a failed query would be a worse
 * failure than a missed acknowledgement.
 *
 * One at a time, oldest first. Two stacked modals is a worse way to read
 * two things than one after the other.
 */
export function SafetySplash() {
  const ports = usePorts();
  const { membership, user, principal } = useSession();
  const [pending, setPending] = useState<readonly SafetyNotice[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kind = principal?.kind ?? membership.kind;

  /**
   * A **subscription**, not a read on mount.
   *
   * A notice put up at nine o'clock has to reach somebody already working,
   * rather than waiting for their next sign-in — which on a site crew that
   * stays signed in all week could be days. It also means the person who
   * published it is asked to acknowledge it like everybody else, which is
   * right: they are in the audience.
   *
   * Server state through a subscription is how the rest of this app reads
   * anything live; this was a one-shot read first and that was wrong for
   * the same reason a stale notice is worse than none.
   */
  useEffect(() => {
    let cancelled = false;

    const recheck = (notices: readonly SafetyNotice[]) => {
      void (async () => {
        // Narrowed before the acknowledgement reads, so those are one per
        // notice this person might actually be shown rather than one per
        // notice the company has ever put up.
        const mine = unacknowledged(notices, kind, new Set(), user.uid);
        if (mine.length === 0) {
          if (!cancelled) setPending([]);
          return;
        }
        try {
          const done = await acknowledgedIds(
            { data: ports.data, companyId: membership.companyId },
            user.uid,
            mine,
          );
          if (!cancelled) setPending(unacknowledged(mine, kind, done, user.uid));
        } catch {
          // Showing it again is the safe direction to be wrong in.
          if (!cancelled) setPending(mine);
        }
      })();
    };

    const unsubscribe = subscribeToSafetyNotices(
      { data: ports.data, companyId: membership.companyId },
      recheck,
      // Logged rather than shown. A splash that cannot load is worth
      // knowing about, but locking somebody out of their work over a
      // failed query would be a worse failure than a missed
      // acknowledgement — so nothing blocks the page beneath.
      (cause) => {
        console.error('[buildmate] safety notices failed to load', cause);
        if (!cancelled) setPending([]);
      },
    );

    return () => {
      // Both halves. Unsubscribing stops new ticks; the flag stops a tick
      // that is already waiting on its acknowledgement reads from calling
      // `setPending` after this component has gone — which is several
      // round trips long and so entirely likely on a company switch.
      cancelled = true;
      unsubscribe();
    };
  }, [ports, membership.companyId, user.uid, kind]);

  const notice = pending[0];
  if (!notice) return null;

  async function acknowledge() {
    setBusy(true);
    setError(null);
    try {
      await acknowledgeSafetyNotice(ports.functions, {
        companyId: membership.companyId,
        noticeId: notice!.id,
      });
      // Taken off the front of the queue rather than reloading: the read
      // is several round trips and the answer is already known.
      setPending((rest) => rest.slice(1));
    } catch {
      setError('Could not record that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      // Escape, the backdrop and the close button are all gone. The only
      // way out is the acknowledgement — see the note above.
      dismissible={false}
      onClose={() => {}}
      title={notice.title}
      subtitle="Please read this before carrying on"
    >
      <div className="flex flex-col gap-4">
        <p className="whitespace-pre-wrap text-sm text-slate-700">{notice.body}</p>

        {pending.length > 1 && (
          <p className="text-xs text-slate-500">
            {pending.length - 1} more {pending.length === 2 ? 'notice' : 'notices'} after this one.
          </p>
        )}

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <div>
          <Button disabled={busy} onClick={() => void acknowledge()}>
            I have read and understood this
          </Button>
        </div>
      </div>
    </Modal>
  );
}
